import { sortBy } from 'lodash';
import moment from 'moment-timezone';
import {
	OpeningHourObject,
	OpeningHourTimes,
	OpeningHourTimesWithMoments,
	LocaleField,
	Languages,
	OpeningHoursWithExceptions,
	ShopPublicInfo,
	Location,
	ShopReceiptInfo,
	OpeningHoursApi,
	ExceptionPeriod,
	FixedTimesObject,
	LocationVisibleChannel,
	LocationUrls,
	LocationWithContact,
} from 'common/types';
import countriesAndTimezones from 'countries-and-timezones';
import { dayTypesToNumbers } from 'common/utils/dateUtils';
import { notUndefined } from './common';

export const get24HourOpeningHoursObject = (): OpeningHoursApi => {
	const openingHourObject: OpeningHourObject = {
		dayType: 'allDays',
		openTime: '00:00',
		closeTime: '23:59',
		isClosed: false,
	};
	return {
		defaultTimes: [openingHourObject],
		exceptionDates: [],
		openingHoursWithLocation: {},
		useDefault: true,
	};
};

export const getOpeningTimesForDate = (
	date: moment.Moment,
	openingHoursWithExceptions: OpeningHoursWithExceptions,
): OpeningHourTimes | null => {
	const { exceptionPeriods, openingHours } = openingHoursWithExceptions;
	const defaultOpeningHours = openingHours || get24HourOpeningHoursObject();
	const activeExceptionPeriods = (exceptionPeriods || []).filter((period) => {
		if (date.isBetween(moment(period.startDate), moment(period.endDate), 'day', '[]')) {
			return true;
		}
		return false;
	});
	const activeOpeningHours = activeExceptionPeriods.length
		? activeExceptionPeriods[0].openingHours
		: defaultOpeningHours;

	const activeExceptionDates = (activeOpeningHours.exceptionDates || []).filter((dateObj) => {
		if (moment(dateObj.date).isSame(date, 'day')) {
			return true;
		}
		return false;
	});

	if (activeExceptionDates.length) {
		const { openTime, closeTime, isClosed } = activeExceptionDates[0];
		return {
			openTime,
			closeTime,
			isClosed,
		};
	} else {
		const dayOfTheWeek = date.day();
		return getOpeningHourObjectFromDefaultTimes(dayOfTheWeek, activeOpeningHours.defaultTimes);
	}
};

export const isWithinOpeningHours = (
	date: moment.Moment,
	timezone: string,
	openingHoursWithExceptions: OpeningHoursWithExceptions,
): boolean => {
	const openingTimes = getOpeningTimesForDate(date, openingHoursWithExceptions);
	if (!openingTimes) return false;
	const { openTime, closeTime, isClosed } = openingTimes;

	if (isClosed) return false;

	const [openHours, openMinutes] = openTime.split(':');
	const openDate = moment(date).tz(timezone).hours(Number(openHours)).minutes(Number(openMinutes));
	const [closeHours, closeMinutes] = closeTime.split(':');
	const closeDate = moment(date)
		.tz(timezone)
		.hours(Number(closeHours))
		.minutes(Number(closeMinutes));

	return date.isBetween(openDate, closeDate);
};

export const getOpeningTimesForDateWithMoments = (
	date: moment.Moment,
	openingHours: OpeningHoursWithExceptions,
): OpeningHourTimesWithMoments | null => {
	const openingTimes = getOpeningTimesForDate(date, openingHours);
	if (!openingTimes) return null;

	const { openTime, closeTime } = openingTimes;
	const parseInt10 = (num: string) => parseInt(num, 10);
	const [openHours, openMinutes] = openTime.split(':').map(parseInt10);
	const [closeHours, closeMinutes] = closeTime.split(':').map(parseInt10);

	return {
		...openingTimes,
		openMoment: moment(date).startOf('day').hours(openHours).minutes(openMinutes),
		closeMoment: moment(date).startOf('day').hours(closeHours).minutes(closeMinutes),
	};
};

export const getOpeningHourObjectFromDefaultTimes = (
	dayNumber: number,
	defaultTimes: OpeningHourObject[],
): OpeningHourTimes | null => {
	const openingTimes = !!defaultTimes?.length
		? defaultTimes
		: get24HourOpeningHoursObject().defaultTimes;
	let selectedOpeningHourObject: OpeningHourObject | null = null;
	for (const openingHourObject of openingTimes) {
		switch (openingHourObject.dayType) {
			case 'monday':
				if (dayNumber === 1) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'tuesday':
				if (dayNumber === 2) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'wednesday':
				if (dayNumber === 3) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'thursday':
				if (dayNumber === 4) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'friday':
				if (dayNumber === 5) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'saturday':
				if (dayNumber === 6) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'sunday':
				if (dayNumber === 0) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			default:
				continue;
		}
	}
	if (selectedOpeningHourObject) {
		const { openTime, closeTime, isClosed } = selectedOpeningHourObject;
		return {
			openTime,
			closeTime,
			isClosed,
		};
	}
	for (const openingHourObject of openingTimes) {
		switch (openingHourObject.dayType) {
			case 'weekDays':
				if (dayNumber > 0 && dayNumber < 6) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			case 'weekends':
				if (dayNumber === 0 || dayNumber === 6) {
					selectedOpeningHourObject = openingHourObject;
				}
				break;
			default:
				continue;
		}
	}
	if (selectedOpeningHourObject) {
		const { openTime, closeTime, isClosed } = selectedOpeningHourObject;
		return {
			openTime,
			closeTime,
			isClosed,
		};
	}
	for (const openingHourObject of openingTimes) {
		switch (openingHourObject.dayType) {
			case 'allDays':
				selectedOpeningHourObject = openingHourObject;
				break;
			default:
				continue;
		}
	}
	if (selectedOpeningHourObject) {
		const { openTime, closeTime, isClosed } = selectedOpeningHourObject;
		return {
			openTime,
			closeTime,
			isClosed,
		};
	}
	return null;
};

export const getCustomMarketingConsent = (
	lang: Languages,
	customMarketingConsent: { text: LocaleField; active: boolean } | null,
): string | null => {
	if (customMarketingConsent && customMarketingConsent.active && customMarketingConsent.text) {
		return customMarketingConsent.text[lang]
			? customMarketingConsent.text[lang]
			: customMarketingConsent.text.def
			? customMarketingConsent.text.def
			: null;
	} else {
		return null;
	}
};

export const getCustomTermsAcceptance = (
	lang: Languages,
	customTermsAcceptance: { text: LocaleField; active: boolean } | null,
): string | null => {
	if (customTermsAcceptance && customTermsAcceptance.active) {
		if (customTermsAcceptance.text && customTermsAcceptance.text[lang]) {
			return customTermsAcceptance.text[lang] || customTermsAcceptance.text.def;
		} else {
			return null;
		}
	} else {
		return null;
	}
};

export const getShopLocations = (
	shopPublicInfo?: ShopPublicInfo,
	channel?: LocationVisibleChannel,
): Location[] =>
	sortBy(
		[shopPublicInfo?.visitingAddress, ...(shopPublicInfo?.shopLocations ?? [])]
			.filter(notUndefined)
			.filter((location) => (channel && location.channels?.includes(channel)) ?? true),
		(location) => {
			return location.orderIndex ?? (location.id === shopPublicInfo?.visitingAddress?.id ? -1 : 0);
		},
	);

export const getShopLocationsWithContact = (
	shopPublicInfo: ShopPublicInfo,
	channel?: LocationVisibleChannel,
): LocationWithContact[] =>
	getShopLocations(shopPublicInfo, channel).map((l) => locationWithContact(l, shopPublicInfo));

export const getShopLocationById = (
	locationId: string,
	shopPublicInfo: ShopPublicInfo,
	channel?: LocationVisibleChannel,
): LocationWithContact | undefined => {
	const shopLocations = getShopLocationsWithContact(shopPublicInfo, channel);
	const shopLocation = shopLocations.find((location) => location.id === locationId);
	return shopLocation;
};

export const locationWithContact = (
	location: Location,
	shopPublicInfo: ShopPublicInfo,
): LocationWithContact => {
	const locationWithContact: LocationWithContact = {
		...location,
		email: location.email ?? shopPublicInfo.email,
		phone: location.phone ?? shopPublicInfo.phone,
	};
	return locationWithContact;
};

export const getCountryTimeZone = (
	country: string | undefined,
	city?: string | undefined,
): string => {
	if (country) {
		const timezonesForCountry = countriesAndTimezones.getTimezonesForCountry(country);
		const guess = moment.tz.guess();
		const countryIncludesGuess =
			timezonesForCountry && timezonesForCountry.filter((timezone) => timezone.name === guess);
		if (countryIncludesGuess && countryIncludesGuess.length) {
			return countryIncludesGuess[0].name;
		}
		if (timezonesForCountry && timezonesForCountry.length) {
			const primaryTimeZone = timezonesForCountry.find((t) => t.aliasOf == null);
			return primaryTimeZone?.name || timezonesForCountry[0].name;
		} else {
			return 'Europe/Helsinki';
		}
	} else {
		return 'Europe/Helsinki';
	}
};

export const getShopReceiptInfo = (
	shopPublicInfo: ShopPublicInfo,
	locationId: string | null,
): ShopReceiptInfo => {
	const { name, phone, businessId, receiptOpenTextfield } = shopPublicInfo;
	const location = locationId ? getShopLocationById(locationId, shopPublicInfo) : undefined;
	return {
		name: location?.name ?? name,
		legalName: name,
		visitingAddress: location ?? null,
		phone: location?.phone ?? phone ?? '',
		businessId: businessId || '',
		receiptOpenTextfield,
	};
};

export const getRentlePaymentReceiptInfo = (): ShopReceiptInfo => {
	return {
		name: 'Rentle',
		legalName: 'Rentle Oy',
		visitingAddress: null,
		phone: '010 299 8200',
		businessId: '2634632-5',
	};
};

export const getOpeningHoursBasedOnLocation = (
	openingHours: OpeningHoursApi | undefined,
	locationId?: string,
): OpeningHoursApi => {
	if (!openingHours) {
		return { defaultTimes: [], exceptionDates: [], openingHoursWithLocation: {}, useDefault: true };
	}

	if (
		!locationId ||
		!openingHours.openingHoursWithLocation ||
		!openingHours.openingHoursWithLocation[locationId] ||
		openingHours.openingHoursWithLocation[locationId].useDefault
	) {
		return {
			...openingHours,
			useDefault: true,
		};
	}

	return { ...openingHours.openingHoursWithLocation[locationId], openingHoursWithLocation: {} };
};

export const getExceptionPeriodsBasedOnLocation = (
	exceptionPeriods: ExceptionPeriod[] | undefined,
	locationId: string,
	defaultLocationId?: string,
	openingHours?: OpeningHoursApi,
) => {
	if (!exceptionPeriods) {
		return [];
	}

	let exceptionPeriodsForLocation: ExceptionPeriod[];

	if (
		defaultLocationId &&
		openingHours &&
		(!openingHours.openingHoursWithLocation ||
			!openingHours.openingHoursWithLocation[locationId] ||
			openingHours.openingHoursWithLocation[locationId].useDefault)
	) {
		exceptionPeriodsForLocation = exceptionPeriods.filter(
			(exceptionPeriodItem) =>
				!exceptionPeriodItem.locationId || exceptionPeriodItem.locationId === defaultLocationId,
		);
	} else {
		exceptionPeriodsForLocation = exceptionPeriods.filter(
			(exceptionPeriodItem) =>
				exceptionPeriodItem.locationId && exceptionPeriodItem.locationId === locationId,
		);
	}

	return exceptionPeriodsForLocation || [];
};

export const getLocationsUsingDefaultOpeningHours = (
	openingHours: OpeningHoursApi,
	locations: Location[],
) => {
	if (!openingHours.openingHoursWithLocation) {
		return locations;
	}

	const filteredLocations = locations.filter(
		(location) =>
			!openingHours.openingHoursWithLocation[location.id] ||
			openingHours.openingHoursWithLocation[location.id].useDefault,
	);

	return filteredLocations;
};

export const getLocationIdByUrl = (
	locationName: string,
	locationUrls: LocationUrls | undefined,
) => {
	return locationUrls?.[locationName] ?? undefined;
};

export const getLocationUrlById = (
	locationId: string,
	locationUrls: LocationUrls | undefined,
): string | undefined => {
	const locationEntries = Object.entries(locationUrls ?? {}).find(([_, id]) => id === locationId);
	return locationEntries?.[0] ?? undefined;
};

export const customFixedDatesOutsideOpeningHours = (
	OpeningHoursWithExceptions: OpeningHoursWithExceptions,
	fixedTime: FixedTimesObject,
) => {
	const openingHoursWithExceptions = getOpeningTimesForDate(
		moment(fixedTime.customDate),
		OpeningHoursWithExceptions,
	);
	if (
		openingHoursWithExceptions &&
		(openingHoursWithExceptions.isClosed ||
			openingHoursWithExceptions.openTime > fixedTime.time ||
			openingHoursWithExceptions.closeTime < fixedTime.time)
	) {
		return true;
	}
	return false;
};

export const isFixedTimeOutsideOpeningHours = (
	openingHoursWithExceptions: OpeningHoursWithExceptions,
	fixedTime: FixedTimesObject,
): boolean => {
	if (fixedTime.dayType === 'custom') {
		return customFixedDatesOutsideOpeningHours(openingHoursWithExceptions, fixedTime);
	}
	const openingHours = openingHoursWithExceptions.openingHours;
	if (!openingHours) {
		return false;
	}
	const arrayOfFixedTimeDays = dayTypesToNumbers[fixedTime.dayType];
	const daysEffected = openingHours.defaultTimes.filter((defaultTime) =>
		dayTypesToNumbers[defaultTime.dayType].some((day) =>
			arrayOfFixedTimeDays.some((fixedTimeDay) => fixedTimeDay === day),
		),
	);
	return daysEffected.some(
		(defaultTime) =>
			defaultTime.isClosed ||
			defaultTime.openTime > fixedTime.time ||
			defaultTime.closeTime < fixedTime.time,
	);
};

export const getFixedTimesOutsideOpeningHours = (
	fixedTimes: FixedTimesObject[] | undefined,
	openingHours: OpeningHoursApi | undefined,
	exeptionPeriods: ExceptionPeriod[],
) => {
	if (!fixedTimes || !openingHours) {
		return undefined;
	}
	const openingHoursWithExceptions = {
		openingHours: openingHours,
		exeptionPeriods: exeptionPeriods,
	};
	const timesOutsideOpeningHours = fixedTimes.filter((fixedTime) =>
		isFixedTimeOutsideOpeningHours(openingHoursWithExceptions, fixedTime),
	);
	return timesOutsideOpeningHours;
};
